import {
  Block,
  Check,
  Close,
  KeyboardArrowDown,
  PauseCircle,
  PriceChange,
  Settings,
  WarningAmber,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ApiCreditAccountAdmin,
  ApiCreditAccountCheckin,
  ApiCreditTransactionAdmin,
  CreditAccountStatus,
} from "griptonite-api-client/types";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useChangeCreditAccountStatusMutation,
  useCreditAccountStatusQuery,
  useCreditAccountTransactionsQuery,
  useCreditSystemAccountsQuery,
  useCreditSystemProductsQuery,
  useVenueCreditSystemQuery,
} from "@/hooks/credits";
import { useCurrentVenueQuery } from "@/hooks/venues";
import pluralize from "@/utils/pluralize";

import Avatar from "../Avatar";
import CopyButton from "../CopyButton";
import { CAP_REASON_LABELS, LogPill } from "../QuestEntries/QuestEntryPanel";
import AccountStatusModal, { RestrictiveStatusAction } from "./AccountStatusModal";
import AdjustmentModal from "./AdjustmentModal";
import { CreditAmount } from "./SortableTable";

const ACCOUNTS_PATH = "/gym/$venueUuid/loyalty/accounts";

const formatTime = (timestamp: string) => format(new Date(timestamp), "d MMM yyyy, HH:mm");

// Laid out as the quest entry panel's log rows are: the label above the time, any pills beside the
// time, and the signed amount at the end. A cap note gets its own row underneath. A spend shows
// its reward's current name, so a rename follows through; the label is the name at the time, and
// stands in until the rewards load or if the reward isn't in the list.
const TransactionRow = ({
  transaction,
  productNames,
  isHighlighted,
  scrollIntoView,
}: {
  transaction: ApiCreditTransactionAdmin;
  productNames?: Map<string, string>;
  isHighlighted: boolean;
  scrollIntoView: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollIntoView) ref.current?.scrollIntoView({ block: "center" });
  }, [scrollIntoView]);

  return (
    <Stack
      ref={ref}
      sx={{ px: 2, py: 1.5, backgroundColor: isHighlighted ? "primary.50" : undefined }}
      gap={0.5}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-sm" fontWeight="lg">
            {(transaction.product && productNames?.get(transaction.product)) ?? transaction.label}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
            {transaction.multiplier !== null ? (
              <LogPill tone="blue">×{transaction.multiplier} boost</LogPill>
            ) : null}
            {transaction.tag === "BONUS" ? <LogPill tone="grey">Side quest</LogPill> : null}
            <Typography level="body-xs" textColor="neutral.500">
              {formatTime(transaction.createdAt)}
              {transaction.staffMember ? ` · by ${transaction.staffMember.name}` : null}
            </Typography>
          </Stack>
        </Box>
        <CreditAmount amount={transaction.amount} />
      </Stack>
      {transaction.requestedAmount !== null ? (
        <Typography level="body-xs" textColor="neutral.500">
          Capped: {pluralize(transaction.requestedAmount, "credit")} were due
          {transaction.capReason
            ? `, but ${CAP_REASON_LABELS[transaction.capReason] ?? transaction.capReason} applied`
            : ""}
        </Typography>
      ) : null}
    </Stack>
  );
};

// A redemption can have more than one transaction, such as the spend and a refund when it's
// voided. All of them are highlighted, and the newest is scrolled to.
const TransactionList = ({
  systemSlug,
  accountUuid,
  productNames,
  highlightRedemptionUuid,
}: {
  systemSlug: string;
  accountUuid: string;
  productNames?: Map<string, string>;
  highlightRedemptionUuid?: string;
}) => {
  const query = useCreditAccountTransactionsQuery(systemSlug, accountUuid);
  const transactions = query.data?.pages.flatMap((page) => page.data);
  const firstHighlightedUuid = highlightRedemptionUuid
    ? transactions?.find((transaction) => transaction.redemptionUuid === highlightRedemptionUuid)
        ?.uuid
    : undefined;

  // Keep loading pages until the highlighted redemption turns up, or the log runs out.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const isSearching = !!highlightRedemptionUuid && !!transactions && !firstHighlightedUuid;
  useEffect(() => {
    if (isSearching && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [isSearching, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (query.error) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Couldn’t load the transactions.
      </Alert>
    );
  }
  if (!transactions) {
    return <CircularProgress sx={{ alignSelf: "center", mt: 4 }} />;
  }
  if (!transactions.length) {
    return (
      <Typography textColor="neutral.500" sx={{ p: 2 }}>
        No transactions yet.
      </Typography>
    );
  }
  return (
    <Stack divider={<Divider />}>
      {transactions.map((transaction) => (
        <TransactionRow
          key={transaction.uuid}
          transaction={transaction}
          productNames={productNames}
          isHighlighted={
            !!highlightRedemptionUuid && transaction.redemptionUuid === highlightRedemptionUuid
          }
          scrollIntoView={transaction.uuid === firstHighlightedUuid}
        />
      ))}
      {query.hasNextPage ? (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Button
            variant="outlined"
            color="neutral"
            loading={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            Load more
          </Button>
        </Box>
      ) : null}
    </Stack>
  );
};

// Freezing and banning ask for confirmation and a reason first; unfreezing needs neither, so it
// goes straight through. A ban can't be lifted here, only by Griptonite staff, so a banned account
// has no menu.
const StatusMenu = ({
  status,
  onRestrict,
  onUnfreeze,
}: {
  status: CreditAccountStatus;
  onRestrict: (action: RestrictiveStatusAction) => void;
  onUnfreeze: () => void;
}) => (
  <Dropdown>
    <MenuButton
      aria-label="Account status actions"
      slots={{ root: IconButton }}
      slotProps={{ root: { color: "neutral", variant: "plain" } }}
    >
      <Settings />
    </MenuButton>
    <Menu size="sm" placement="bottom-end">
      {status === "FROZEN" ? (
        <MenuItem onClick={onUnfreeze}>Unfreeze account</MenuItem>
      ) : (
        <MenuItem onClick={() => onRestrict("freeze")}>Freeze account</MenuItem>
      )}
      <MenuItem color="danger" onClick={() => onRestrict("ban")}>
        Ban account
      </MenuItem>
    </Menu>
  </Dropdown>
);

// Says when and why a frozen or banned account was restricted. The headline comes from the
// accounts list, so it shows straight away; the rest follows once the change has loaded.
const StatusNotice = ({ account }: { account: ApiCreditAccountAdmin }) => {
  const { data } = useCreditAccountStatusQuery(account.uuid, account.status);
  if (account.status === "ACTIVE") return null;

  // The data can be the account's previous status for a moment after it changes.
  const change = data?.status === account.status ? data.statusChange : null;
  const isBanned = account.status === "BANNED";

  return (
    <Alert
      color="danger"
      variant="soft"
      invertedColors
      startDecorator={isBanned ? <Block /> : <PauseCircle />}
      sx={{ alignItems: "flex-start" }}
    >
      <Stack gap={0.25}>
        <Typography level="title-sm" fontWeight="lg">
          {isBanned ? "Banned" : "Frozen"}
          {change ? ` on ${format(new Date(change.timestamp), "d MMM yyyy, HH:mm")}` : null}
          {change?.staffMember ? ` by ${change.staffMember.name}` : null}
        </Typography>
        {change?.reason ? <Typography level="body-sm">Reason: {change.reason}</Typography> : null}
        {isBanned ? (
          <Typography level="body-sm">Contact support if you need to lift the ban.</Typography>
        ) : null}
      </Stack>
    </Alert>
  );
};

const AccountSummary = ({
  account,
  onAdjust,
}: {
  account: ApiCreditAccountAdmin;
  onAdjust: () => void;
}) => (
  <Stack gap={1.5} sx={{ p: 2, borderBottom: "1px solid", borderColor: "neutral.200" }}>
    <StatusNotice account={account} />
    <Stack direction="row" alignItems="flex-end" justifyContent="space-between" gap={2}>
      <Box>
        <Typography level="body-sm" textColor="neutral.500">
          Balance
        </Typography>
        <Typography level="h2">{account.balance}</Typography>
      </Box>
      <Button
        variant="outlined"
        color="neutral"
        startDecorator={<PriceChange />}
        onClick={onAdjust}
      >
        Adjust balance
      </Button>
    </Stack>
    {account.nextExpiryAt ? (
      <Typography level="body-sm">
        {pluralize(account.nextExpiryAmount, "credit")} expire on{" "}
        {format(new Date(account.nextExpiryAt), "d MMM yyyy")}
      </Typography>
    ) : null}
  </Stack>
);

const DetailRow = ({ label, value }: { label: string; value: string | null }) => (
  <>
    <Typography level="body-sm" textColor="neutral.500">
      {label}
    </Typography>
    {value ? (
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
        <Typography level="body-sm" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          {value}
        </Typography>
        <CopyButton value={value} label={label.toLowerCase()} />
      </Stack>
    ) : (
      <Typography level="body-sm" textColor="neutral.500">
        —
      </Typography>
    )}
  </>
);

const MembershipDetails = ({ checkinAccount }: { checkinAccount: ApiCreditAccountCheckin }) => {
  const membershipName = [checkinAccount.firstName, checkinAccount.lastName]
    .filter(Boolean)
    .join(" ");
  // Some providers use the barcode as the customer number, so show it once when they match.
  const isSameNumber =
    checkinAccount.barcode !== null && checkinAccount.barcode === checkinAccount.customerNumber;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: 2,
        rowGap: 0.5,
        pl: 1.5,
        borderLeft: "2px solid",
        borderColor: "neutral.200",
      }}
    >
      <DetailRow label="Name" value={membershipName} />
      {isSameNumber ? (
        <DetailRow label="Barcode / customer no." value={checkinAccount.barcode} />
      ) : (
        <>
          <DetailRow label="Barcode" value={checkinAccount.barcode} />
          <DetailRow label="Customer number" value={checkinAccount.customerNumber} />
        </>
      )}
    </Box>
  );
};

// Who the member is, under their name: email, verification and check-in account.
const MemberInfo = ({ account }: { account: ApiCreditAccountAdmin }) => {
  const { user, checkinAccount } = account;
  const { data: venue } = useCurrentVenueQuery();
  const [isExpanded, setIsExpanded] = useState(false);

  // A linked account names its own provider. Otherwise use this venue's, as the credit system
  // settings do.
  const providerName =
    checkinAccount?.providerDisplayName ?? venue?.checkinProvider?.displayName ?? "Membership";

  return (
    <Stack gap={0.75}>
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>
          {user.email}
        </Typography>
        <CopyButton value={user.email} label="email" />
      </Stack>
      {user.parent ? (
        <Typography level="body-sm" textColor="neutral.500">
          Managed by {user.parent.name}
        </Typography>
      ) : null}
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Chip
          size="sm"
          variant="soft"
          color={account.emailVerified ? "success" : "warning"}
          startDecorator={account.emailVerified ? <Check /> : <WarningAmber />}
          sx={{ fontWeight: "lg" }}
        >
          {account.emailVerified ? "Email verified" : "Email unverified"}
        </Chip>
        <Chip
          size="sm"
          variant="soft"
          color={checkinAccount ? "success" : "neutral"}
          startDecorator={checkinAccount ? <Check /> : null}
          sx={{ fontWeight: "lg" }}
        >
          {providerName} {checkinAccount ? "linked" : "not linked"}
        </Chip>
        {checkinAccount ? (
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            endDecorator={
              <KeyboardArrowDown
                fontSize="small"
                sx={{
                  transition: "transform 0.15s",
                  transform: isExpanded ? "rotate(180deg)" : null,
                }}
              />
            }
            sx={{
              ml: "auto",
              minHeight: 0,
              py: 0.25,
              px: 1,
              gap: 0.25,
              fontWeight: "md",
              color: "neutral.500",
            }}
          >
            Details
          </Button>
        ) : null}
      </Stack>
      {checkinAccount && isExpanded ? <MembershipDetails checkinAccount={checkinAccount} /> : null}
    </Stack>
  );
};

export default function AccountPanel({
  accountUuid,
  highlightRedemptionUuid,
}: {
  accountUuid: string;
  highlightRedemptionUuid?: string;
}) {
  const navigate = useNavigate();
  const { data: venue } = useCurrentVenueQuery();
  const { data: system } = useVenueCreditSystemQuery(venue?.uuid);
  // There's no endpoint for one account, so it comes from the list the table already loaded.
  const accountsQuery = useCreditSystemAccountsQuery(system?.slug);
  const account = accountsQuery.data?.find((item) => item.uuid === accountUuid);
  const productsQuery = useCreditSystemProductsQuery(system?.slug, venue?.uuid);
  const productNames = useMemo(
    () => productsQuery.data && new Map(productsQuery.data.map((p) => [p.uuid, p.name])),
    [productsQuery.data],
  );
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [statusAction, setStatusAction] = useState<RestrictiveStatusAction | null>(null);
  const statusMutation = useChangeCreditAccountStatusMutation(system?.slug ?? "", accountUuid);
  const resetStatusMutation = statusMutation.reset;
  // A failed unfreeze shouldn't carry over to the next account that opens.
  useEffect(() => resetStatusMutation(), [accountUuid, resetStatusMutation]);

  let content;
  if (accountsQuery.error) {
    content = (
      <Alert color="danger" sx={{ m: 2 }}>
        Couldn’t load the account.
      </Alert>
    );
  } else if (accountsQuery.data && !account) {
    content = (
      <Alert color="neutral" sx={{ m: 2 }}>
        This account doesn’t exist.
      </Alert>
    );
  } else if (system && account) {
    content = (
      <>
        {statusMutation.error ? (
          <Alert color="danger" sx={{ m: 2, mb: 0 }}>
            Couldn’t unfreeze the account. Please try again.
          </Alert>
        ) : null}
        <AccountSummary account={account} onAdjust={() => setIsAdjusting(true)} />
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <TransactionList
            systemSlug={system.slug}
            accountUuid={account.uuid}
            productNames={productNames}
            highlightRedemptionUuid={highlightRedemptionUuid}
          />
        </Box>
        {isAdjusting ? (
          <AdjustmentModal
            account={account}
            systemSlug={system.slug}
            maxAccountBalance={system.maxAccountBalance}
            onClose={() => setIsAdjusting(false)}
          />
        ) : null}
        {statusAction ? (
          <AccountStatusModal
            account={account}
            systemSlug={system.slug}
            action={statusAction}
            onClose={() => setStatusAction(null)}
          />
        ) : null}
      </>
    );
  } else {
    content = <CircularProgress sx={{ alignSelf: "center", mt: 4 }} />;
  }

  return (
    <Stack
      sx={{
        width: 420,
        flexShrink: 0,
        height: "100%",
        borderLeft: "1px solid",
        borderColor: "neutral.200",
        backgroundColor: "background.surface",
      }}
    >
      <Stack gap={1} sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "neutral.200" }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          {account ? <Avatar user={account.user} size={32} /> : null}
          <Typography level="title-lg" noWrap sx={{ flex: 1 }}>
            {account?.user.name ?? "Account"}
          </Typography>
          {account && system && account.status !== "BANNED" ? (
            <StatusMenu
              status={account.status}
              onRestrict={(action) => {
                statusMutation.reset();
                setStatusAction(action);
              }}
              onUnfreeze={() => statusMutation.mutate({ action: "unfreeze" })}
            />
          ) : null}
          <IconButton
            aria-label="Close"
            onClick={() =>
              navigate({ to: ACCOUNTS_PATH, params: { venueUuid: venue?.uuid ?? "" } })
            }
          >
            <Close />
          </IconButton>
        </Stack>
        {/* The full width under the name, so the chips and details button share a line. Keyed so
            the details close again when another account opens. */}
        {account ? <MemberInfo key={account.uuid} account={account} /> : null}
      </Stack>
      {content}
    </Stack>
  );
}
